import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { Badge, Banner, Button, Field, Label, Mono, Textarea } from "../ui/Field";
import { AssetPicker } from "../ui/AssetPicker";
import { cn } from "../ui/cn";
import { renderBlock as renderBlockHtml, renderHeader as renderHeaderHtml, renderFooter as renderFooterHtml, type Block as RenderBlock, type SiteSettings } from "../lib/render";
// @ts-ignore - vite ?raw import
import neueraCss from "../neuera-assets/neuera.css?raw";
// @ts-ignore - vite ?raw import
import gridShimCss from "../neuera-assets/grid-shim.css?raw";

type PageDoc = {
  id: string;
  slug: string;
  title: string;
  status: string;
  draft_revision: number;
  live_revision: number | null;
  publish_at: number | null;
  revision: {
    revision: number;
    metadata: PageMetadata;
    header: unknown;
    footer: unknown;
    blocks: Block[];
  } | null;
};

type PageMetadata = {
  title: string;
  slug: string;
  description?: string;
  canonical?: string | null;
  noindex?: boolean;
  behavior?: { pageStyle?: string; showFooterCta?: boolean; showPageHeader?: boolean; customCssClass?: string; redirectTo?: string; excludeFromBuild?: boolean };
};

type Block = {
  id: string;
  componentKey: string;
  props: Record<string, unknown>;
  mobile?: { props?: Record<string, unknown> };
  visibility?: { desktop?: boolean; mobile?: boolean };
};

const BUILT_INS = [
  { key: "hero.neuera", name: "Hero", icon: "◆" },
  { key: "hero.centered", name: "Hero · centered", icon: "◇" },
  { key: "hero.split", name: "Hero · split", icon: "⬒" },
  { key: "trust_strip", name: "Trust strip", icon: "═" },
  { key: "cards.neuera", name: "Cards (neuera)", icon: "▦" },
  { key: "cards.grid", name: "Cards grid", icon: "⊟" },
  { key: "features", name: "Features", icon: "✦" },
  { key: "steps", name: "Steps", icon: "▷" },
  { key: "testimonials", name: "Testimonials", icon: "❝" },
  { key: "faq", name: "FAQ", icon: "?" },
  { key: "cta", name: "Call to action", icon: "→" },
  { key: "programs_list", name: "Programs list", icon: "≣" },
  { key: "services", name: "Services", icon: "⌘" },
  { key: "pricing", name: "Pricing", icon: "₹" },
  { key: "office_info", name: "Office info", icon: "⏚" },
  { key: "contact_form", name: "Contact form", icon: "✉" },
  { key: "newsletter", name: "Newsletter", icon: "♨" },
  { key: "content.section", name: "Content section", icon: "▭" },
  { key: "content.richtext", name: "Rich text", icon: "¶" },
  { key: "content.feature_split", name: "Feature split", icon: "⬓" },
  { key: "content.image", name: "Image", icon: "▣" },
  { key: "form.builder", name: "Form (generic)", icon: "⏍" },
  { key: "layout.spacer", name: "Spacer", icon: "—" },
];

function defaultPropsFor(key: string): Record<string, unknown> {
  switch (key) {
    case "hero.neuera": return { heading: "Care that fits your life.", subheading: "Subheading text.", badge_text: "", cta_text: "Book a Consult", cta_link: "#book", secondary_cta_text: "", secondary_cta_link: "", theme: "light" };
    case "hero.centered": return { title: "Heading", subtitle: "Subheading" };
    case "hero.split": return { title: "Heading", body: "Body text", ctaText: "Get started", ctaLink: "/" };
    case "trust_strip": return { items: [{ icon: "bi-shield-check", label: "Clinician-led" }, { icon: "bi-heart", label: "Compassionate" }, { icon: "bi-lock", label: "Private & secure" }] };
    case "cards.neuera": return { heading: "Section heading", subheading: "", columns: "3", style: "default", cards: [{ title: "Card", description: "Description" }] };
    case "cards.grid": return { columns: 3, items: [{ heading: "Card", body: "Description" }] };
    case "features": return { heading: "Features", features: [{ icon: "bi-clock", title: "Flexible", description: "Description" }] };
    case "steps": return { heading: "Your Care Ecosystem", subheading: "", steps: [{ step_number: "1", title: "Step one", description: "Description" }, { step_number: "2", title: "Step two", description: "Description" }, { step_number: "3", title: "Step three", description: "Description" }] };
    case "testimonials": return { heading: "What patients say", theme: "light", testimonials: [{ quote: "Great experience.", author_name: "Anonymous", author_role: "Patient" }] };
    case "faq": return { heading: "Common questions", items: [{ question: "Question?", answer: "<p>Answer.</p>" }] };
    case "cta": return { heading: "Ready to start?", description: "Book a consultation.", button_text: "Book", button_link: "#book", style: "primary" };
    case "programs_list": return { heading: "Programs", programs: [{ name: "Program", tagline: "Tagline", description: "Description", duration: "3 months", color: "terracotta" }] };
    case "services": return { heading: "Services", categories: [{ category_name: "Consultations", services: [{ name: "First consult", price: "₹1,499", description: "45 min" }] }] };
    case "pricing": return { heading: "Pricing", tiers: [{ name: "Basic", duration: "1 month", description: "", features: ["Feature 1"], is_popular: false, cta_text: "Choose", cta_link: "/contact" }] };
    case "office_info": return { schedule_heading: "Office hours", schedule: [{ days: "Monday–Friday", hours: "9–5 IST" }], areas_heading: "Service areas", service_areas: [{ region: "Delhi-NCR", cities: "Delhi, Noida, Gurgaon" }] };
    case "contact_form": return { heading: "Send Us a Message", description: "We'll respond within 24 hours.", submit_button_text: "Send Message", success_message: "Thanks!" };
    case "newsletter": return { heading: "Stay in the loop", description: "Get updates from our team.", button_text: "Subscribe" };
    case "content.section": return { heading: "Section heading", paragraphs: ["Lorem ipsum dolor sit amet."] };
    case "content.richtext": return { html: "<p>Write something…</p>" };
    case "content.feature_split": return { heading: "Feature heading", description: "Description.", imagePosition: "right" };
    case "content.image": return { assetId: "", alt: "Image", caption: "" };
    case "form.builder": return { title: "Contact us", fields: [{ kind: "text", name: "name", label: "Name", required: true }, { kind: "email", name: "email", label: "Email", required: true }], submit: { label: "Send" }, delivery: { url: "" }, successMessage: "Thanks!", errorMessage: "Try again." };
    case "layout.spacer": return { height: "medium", style: "blank" };
    default: return {};
  }
}

// Render the current draft as a full page in a new tab. The HTML is built
// client-side using the same renderer the publish pipeline uses, plus the
// neuera + grid-shim CSS inlined so the blob URL is self-contained.
function openPreview(doc: PageDoc | null, settings: SiteSettings | null) {
  if (!doc?.revision) return;
  const blocks = doc.revision.blocks ?? [];
  const chrome = settings?.chrome;
  const md = doc.revision.metadata;
  const assetUrl = (id: string) => id; // already-resolved URLs flow through
  const blockHtml = blocks
    .filter((b) => b.visibility?.desktop !== false)
    .map((b) => renderBlockHtml(b as RenderBlock, "desktop", {}, assetUrl))
    .join("\n");
  const pageStyle = md.behavior?.pageStyle;
  const header = chrome && pageStyle !== "landing" ? renderHeaderHtml(chrome, doc.slug) : "";
  const footer = chrome && pageStyle !== "landing" ? renderFooterHtml(chrome, { showFooterCta: md.behavior?.showFooterCta !== false }) : "";
  const title = String(md.title || doc.title || "Preview");
  const html = `<!doctype html><html lang="en"><head><meta charset="utf-8" /><meta name="viewport" content="width=device-width, initial-scale=1" /><title>${escapeForAttr(title)} · preview</title>${md.description ? `<meta name="description" content="${escapeForAttr(String(md.description))}" />` : ""}<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400..700;1,400..700&family=Source+Sans+3:ital,wght@0,300..700;1,300..700&display=swap" /><link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.css" /><script src="https://cdn.jsdelivr.net/npm/@tailwindcss/browser@4"></script><style>${neueraCss as string}\n${gridShimCss as string}</style></head><body class="${escapeForAttr(md.behavior?.customCssClass ?? "")}">${header}<main>${blockHtml}</main>${footer}</body></html>`;
  const url = URL.createObjectURL(new Blob([html], { type: "text/html;charset=utf-8" }));
  const win = window.open(url, "_blank", "noopener,noreferrer");
  // Revoke after the tab has had a chance to load (best-effort).
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
  if (!win) alert("Preview blocked — allow pop-ups for this site.");
}
function escapeForAttr(s: string): string { return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;"); }

export default function PageEditor() {
  const { slug: siteSlug, page: pageId } = useParams();
  const [doc, setDoc] = useState<PageDoc | null>(null);
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [viewport, setViewport] = useState<"desktop" | "mobile">("desktop");
  const [showBlockPicker, setShowBlockPicker] = useState(false);
  const saveTimer = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await api.get<PageDoc>(`/api/sites/${siteSlug}/pages/${pageId}`);
      setDoc(d);
      setDirty(false);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, [siteSlug, pageId]);

  useEffect(() => { if (siteSlug && pageId) load(); }, [load, siteSlug, pageId]);
  useEffect(() => {
    if (!siteSlug) return;
    api.get<SiteSettings>(`/api/sites/${siteSlug}/settings`).then(setSettings).catch(() => setSettings(null));
  }, [siteSlug]);

  async function save() {
    if (!doc?.revision) return;
    setBusy(true); setErr(null);
    try {
      await api.put(`/api/sites/${siteSlug}/pages/${pageId}/draft`, {
        metadata: doc.revision.metadata,
        header: doc.revision.header,
        footer: doc.revision.footer,
        blocks: doc.revision.blocks,
      });
      setDirty(false);
      await load();
    } catch (e) { setErr(e instanceof ApiError ? e.message : (e as Error).message); }
    finally { setBusy(false); }
  }

  async function publish() {
    if (dirty) await save();
    setBusy(true);
    try { await api.post(`/api/sites/${siteSlug}/pages/${pageId}/publish`); await load(); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  async function submitReview() {
    try { await api.post(`/api/sites/${siteSlug}/pages/${pageId}/submit-review`); await load(); }
    catch (e) { setErr((e as Error).message); }
  }

  useEffect(() => {
    if (!dirty) return;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => save(), 1500);
    return () => { if (saveTimer.current) window.clearTimeout(saveTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        if (dirty) save();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty]);

  function updateBlocks(fn: (blocks: Block[]) => Block[]) {
    setDoc((d) => d?.revision ? { ...d, revision: { ...d.revision, blocks: fn(d.revision.blocks) } } : d);
    setDirty(true);
  }
  function updateMetadata(patch: Partial<PageMetadata>) {
    setDoc((d) => d?.revision ? { ...d, revision: { ...d.revision, metadata: { ...d.revision.metadata, ...patch } } } : d);
    setDirty(true);
  }

  if (err && !doc) return <div className="p-12"><Banner kind="error">{err}</Banner></div>;
  if (!doc) return <div className="p-12"><div className="h-32 animate-pulse bg-[var(--color-paper-2)] rounded" /></div>;

  const blocks = doc.revision?.blocks ?? [];
  const selectedBlock = blocks.find((b) => b.id === selected) ?? null;

  return (
    <div className="flex flex-col h-screen bg-[var(--color-paper)]">
      {/* Top bar */}
      <header className="border-b border-[var(--color-rule)] bg-[var(--color-paper-2)]/60 backdrop-blur-sm">
        <div className="px-6 py-3 flex items-center gap-4 min-w-0">
          <Link to={`/sites/${siteSlug}/pages`} className="text-[var(--color-muted)] hover:text-[var(--color-ink)] text-[12px] flex items-center gap-1 no-underline">
            <span>←</span> Pages
          </Link>
          <div className="h-4 w-px bg-[var(--color-rule)]" />
          <div className="min-w-0 flex-1">
            <h1 className="display text-[22px] leading-tight text-[var(--color-ink)] truncate">{doc.title || "Untitled"}</h1>
            <div className="flex items-center gap-3 text-[11px] text-[var(--color-muted)] font-mono mt-0.5">
              <span>/{doc.slug}</span>
              <span>·</span>
              <SaveIndicator dirty={dirty} busy={busy} />
              <span>·</span>
              <span>draft #{doc.draft_revision}</span>
              {doc.live_revision && (<><span>·</span><span>live #{doc.live_revision}</span></>)}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <ViewportToggle value={viewport} onChange={setViewport} />
            <div className="h-4 w-px bg-[var(--color-rule)] mx-1" />
            <Button variant="ghost" size="sm" onClick={async () => {
              try {
                const r = await api.post<{ url: string }>(`/api/sites/${siteSlug}/pages/${pageId}/preview-link`, { ttl_seconds: 60 * 60 * 24 });
                navigator.clipboard?.writeText(r.url).catch(() => undefined);
                alert(`Preview link copied:\n${r.url}`);
              } catch (e) { alert((e as Error).message); }
            }}>Share preview</Button>
            <Link to={`/sites/${siteSlug}/pages/${pageId}/revisions`} className="inline-flex h-7 items-center px-3 rounded-[var(--radius-sm)] border border-[var(--color-rule)] text-[12.5px] no-underline text-[var(--color-ink)] hover:bg-[var(--color-paper-2)] transition-colors">History</Link>
            <Button variant="ghost" size="sm" onClick={() => openPreview(doc, settings)} disabled={!doc?.revision}>Preview</Button>
            <Button variant="ghost" size="sm" onClick={submitReview}>Submit for review</Button>
            <Button size="sm" onClick={publish} disabled={busy}>Publish ↗</Button>
          </div>
        </div>
        {err && (
          <div className="px-6 pb-3"><Banner kind="error">{err}</Banner></div>
        )}
      </header>

      {/* Three-pane workspace */}
      <div className="flex-1 grid grid-cols-[260px_1fr_320px] min-h-0">
        {/* Outline */}
        <aside className="border-r border-[var(--color-rule)] bg-[var(--color-paper-2)]/40 flex flex-col min-h-0">
          <div className="px-4 pt-4 pb-2 flex items-center justify-between">
            <div className="label-tracked">Outline</div>
            <span className="font-mono text-[10.5px] text-[var(--color-muted)]">{blocks.length}</span>
          </div>
          <ol className="flex-1 overflow-y-auto px-2 pb-3">
            {blocks.length === 0 && (
              <div className="px-3 py-6 text-[12px] text-[var(--color-muted)] text-center">
                Empty page.<br/>Add a block below.
              </div>
            )}
            {blocks.map((b, i) => {
              const isSelected = selected === b.id;
              const hidden = b.visibility?.[viewport] === false;
              return (
                <li key={b.id}>
                  <button
                    onClick={() => setSelected(b.id)}
                    className={cn(
                      "w-full text-left px-3 py-2 rounded-[3px] flex items-baseline gap-2.5 transition-colors group",
                      isSelected
                        ? "bg-[var(--color-surface)] text-[var(--color-ink)] border-l-2 border-[var(--color-accent)] rounded-l-none pl-[10px]"
                        : "hover:bg-[var(--color-surface)]/70",
                      hidden && "opacity-50",
                    )}
                  >
                    <span className="font-mono text-[10px] text-[var(--color-muted)] w-5 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[13px] text-[var(--color-ink)] truncate">{niceName(b.componentKey)}</span>
                      <span className="block text-[10.5px] text-[var(--color-muted)] font-mono truncate">{b.componentKey}</span>
                    </span>
                    {hidden && <span className="text-[10px] text-[var(--color-amber)]">hidden</span>}
                  </button>
                </li>
              );
            })}
          </ol>
          {/* Add block */}
          <div className="border-t border-[var(--color-rule)] p-3 relative">
            <button
              onClick={() => setShowBlockPicker((s) => !s)}
              className="w-full inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-[var(--radius-sm)] bg-[var(--color-ink)] text-[var(--color-paper)] text-[12.5px] hover:bg-[var(--color-ink-2)] transition-colors"
            >
              <span>+</span> Add block
            </button>
            {showBlockPicker && (
              <div className="absolute left-3 right-3 bottom-12 bg-[var(--color-surface)] border border-[var(--color-rule)] rounded-[var(--radius-sm)] shadow-[var(--shadow-md)] max-h-[280px] overflow-y-auto z-10">
                {BUILT_INS.map((b) => (
                  <button
                    key={b.key}
                    onClick={() => {
                      const id = `blk-${Math.random().toString(36).slice(2, 10)}`;
                      updateBlocks((bs) => [...bs, { id, componentKey: b.key, props: defaultPropsFor(b.key) }]);
                      setSelected(id);
                      setShowBlockPicker(false);
                    }}
                    className="w-full text-left flex items-baseline gap-3 px-3 py-2 hover:bg-[var(--color-paper-2)] transition-colors"
                  >
                    <span className="font-mono text-[14px] text-[var(--color-accent)] w-4">{b.icon}</span>
                    <span className="text-[13px] text-[var(--color-ink)]">{b.name}</span>
                    <span className="ml-auto font-mono text-[10.5px] text-[var(--color-mute-2)]">{b.key}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </aside>

        {/* Canvas */}
        <section className="overflow-y-auto bg-paper-noise">
          <div className={cn("mx-auto py-10 px-8 transition-[max-width] duration-200", viewport === "mobile" ? "max-w-[420px]" : "max-w-[1100px]")}>
            <div className="bg-[var(--color-surface)] rounded-[var(--radius-sm)] shadow-[var(--shadow-md)] border border-[var(--color-rule)] overflow-hidden min-h-[500px]">
              {blocks.length === 0 ? (
                <div className="py-20 px-8 text-center">
                  <div className="display text-3xl text-[var(--color-muted)] mb-2">Empty canvas.</div>
                  <p className="text-[13px] text-[var(--color-muted)]">Add a block from the outline panel.</p>
                </div>
              ) : (
                <div>
                  {settings?.chrome && <ChromeIframe kind="header" chrome={settings.chrome} currentSlug={doc?.slug} />}
                  {blocks.map((b) => (
                    <BlockPreview
                      key={b.id}
                      block={b}
                      viewport={viewport}
                      selected={selected === b.id}
                      onClick={() => setSelected(b.id)}
                    />
                  ))}
                  {settings?.chrome && <ChromeIframe kind="footer" chrome={settings.chrome} showFooterCta={doc?.revision?.metadata.behavior?.showFooterCta !== false} />}
                </div>
              )}
            </div>
            <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-mute-2)] text-center mt-4">
              {viewport === "mobile" ? "Mobile · 420px" : "Desktop · 1100px"}
            </div>
          </div>
        </section>

        {/* Inspector */}
        <aside className="border-l border-[var(--color-rule)] bg-[var(--color-paper-2)]/40 overflow-y-auto">
          {selectedBlock ? (
            <Inspector
              key={selectedBlock.id}
              block={selectedBlock}
              viewport={viewport}
              onChange={(b) => updateBlocks((bs) => bs.map((x) => (x.id === b.id ? b : x)))}
              onDelete={() => { updateBlocks((bs) => bs.filter((x) => x.id !== selectedBlock.id)); setSelected(null); }}
              onMoveUp={() => updateBlocks((bs) => move(bs, selectedBlock.id, -1))}
              onMoveDown={() => updateBlocks((bs) => move(bs, selectedBlock.id, +1))}
              onDuplicate={() => updateBlocks((bs) => duplicate(bs, selectedBlock.id))}
            />
          ) : (
            <PageSettings metadata={doc.revision?.metadata ?? { title: doc.title, slug: doc.slug }} onChange={updateMetadata} />
          )}
        </aside>
      </div>
    </div>
  );
}

function niceName(key: string): string {
  return key.split(".").map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(" · ");
}

function move<T extends { id: string }>(xs: T[], id: string, delta: number): T[] {
  const i = xs.findIndex((x) => x.id === id);
  if (i < 0) return xs;
  const j = Math.max(0, Math.min(xs.length - 1, i + delta));
  if (i === j) return xs;
  const copy = xs.slice();
  const [it] = copy.splice(i, 1);
  copy.splice(j, 0, it!);
  return copy;
}

function duplicate(xs: Block[], id: string): Block[] {
  const i = xs.findIndex((x) => x.id === id);
  if (i < 0) return xs;
  const original = xs[i];
  if (!original) return xs;
  const dup: Block = { ...original, id: `blk-${Math.random().toString(36).slice(2, 10)}` };
  const copy = xs.slice();
  copy.splice(i + 1, 0, dup);
  return copy;
}

function ViewportToggle({ value, onChange }: { value: "desktop" | "mobile"; onChange: (v: "desktop" | "mobile") => void }) {
  return (
    <div className="inline-flex border border-[var(--color-rule)] rounded-[var(--radius-sm)] overflow-hidden">
      <button
        onClick={() => onChange("desktop")}
        className={cn(
          "h-7 px-3 text-[11.5px] font-medium uppercase tracking-[0.04em] transition-colors",
          value === "desktop" ? "bg-[var(--color-ink)] text-[var(--color-paper)]" : "bg-transparent text-[var(--color-muted)] hover:text-[var(--color-ink)]"
        )}
      >Desktop</button>
      <button
        onClick={() => onChange("mobile")}
        className={cn(
          "h-7 px-3 text-[11.5px] font-medium uppercase tracking-[0.04em] transition-colors",
          value === "mobile" ? "bg-[var(--color-ink)] text-[var(--color-paper)]" : "bg-transparent text-[var(--color-muted)] hover:text-[var(--color-ink)]"
        )}
      >Mobile</button>
    </div>
  );
}

function SaveIndicator({ dirty, busy }: { dirty: boolean; busy: boolean }) {
  if (busy) return <span className="text-[var(--color-muted)] flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[var(--color-amber)] pulse-dot" />Saving…</span>;
  if (dirty) return <span className="text-[var(--color-amber)]">Unsaved</span>;
  return <span className="text-[var(--color-leaf)] flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-[var(--color-leaf)]" />Saved</span>;
}

function BlockPreview({ block, viewport, selected, onClick }: { block: Block; viewport: "desktop" | "mobile"; selected: boolean; onClick: () => void }) {
  const hidden = block.visibility?.[viewport] === false;
  if (hidden) {
    return (
      <div onClick={onClick} className={cn("px-6 py-3 border-b border-[var(--color-rule)] bg-hatch text-[11px] font-mono text-[var(--color-muted)] cursor-pointer", selected && "ring-2 ring-[var(--color-accent)] ring-inset")}>
        Hidden on {viewport} · {block.componentKey}
      </div>
    );
  }
  const props = viewport === "mobile" && block.mobile?.props ? { ...block.props, ...block.mobile.props } : block.props;
  return (
    <div
      onClick={onClick}
      className={cn(
        "relative cursor-pointer border-b border-[var(--color-rule)] transition-all",
        selected && "ring-2 ring-[var(--color-accent)] ring-inset"
      )}
    >
      {selected && (
        <div className="absolute top-2 left-2 z-10 inline-flex items-center px-2 py-0.5 rounded-[3px] bg-[var(--color-accent)] text-[var(--color-paper)] text-[10px] font-mono uppercase tracking-[0.06em]">
          Selected
        </div>
      )}
      <BlockIframe block={{ ...block, props }} viewport={viewport} />
    </div>
  );
}

// Render a single block via the publish renderer inside a sandbox iframe so
// neuera.css applies cleanly without colliding with the admin's design system.
function BlockIframe({ block, viewport }: { block: Block; viewport: "desktop" | "mobile" }) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(240);

  const html = useMemo(() => {
    const assetUrl = (id: string) => id; // image-resolved URLs flow through unchanged
    const blockHtml = renderBlockHtml(block as RenderBlock, viewport, {}, assetUrl);
    return `<!doctype html><html><head><meta charset="utf-8" /><meta name="viewport" content="width=device-width, initial-scale=1" /><link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400..700;1,400..700&family=Source+Sans+3:ital,wght@0,300..700;1,300..700&display=swap" /><link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.css" /><script src="https://cdn.jsdelivr.net/npm/@tailwindcss/browser@4"></script><style>${neueraCss as string}\n${gridShimCss as string}\nhtml,body{background:transparent !important;margin:0 !important;padding:0 !important;overflow-x:hidden}.nl-header,.nl-footer{display:none !important}/* Force scroll-reveal elements visible inside the editor iframe (no scroll happens here) */.reveal,.reveal-scale,.reveal-on-scroll,.n-reveal,.n-reveal-scale,.n-reveal-left,.n-reveal-right,.n-reveal-blur{opacity:1 !important;transform:none !important;transition:none !important}</style></head><body>${blockHtml}</body></html>`;
  }, [block, viewport]);

  useLayoutEffect(() => {
    const iframe = ref.current;
    if (!iframe) return;
    let interval: ReturnType<typeof setInterval> | null = null;
    let stopTimer: ReturnType<typeof setTimeout> | null = null;
    let lastReported = 0;
    const measure = () => {
      const doc = iframe.contentDocument;
      if (!doc?.body) return;
      const h = Math.max(doc.documentElement.scrollHeight, doc.body.scrollHeight);
      if (h && h !== lastReported) {
        lastReported = h;
        setHeight(h);
      }
    };
    const start = () => {
      measure();
      // Re-measure for ~4 s as web fonts, Tailwind browser, and images settle in.
      if (interval) clearInterval(interval);
      interval = setInterval(measure, 200);
      if (stopTimer) clearTimeout(stopTimer);
      stopTimer = setTimeout(() => { if (interval) { clearInterval(interval); interval = null; } }, 4000);
    };
    iframe.addEventListener("load", start);
    // If srcDoc was set synchronously and load already fired, kick off measurement now.
    if (iframe.contentDocument?.readyState === "complete") start();
    return () => {
      iframe.removeEventListener("load", start);
      if (interval) clearInterval(interval);
      if (stopTimer) clearTimeout(stopTimer);
    };
  }, [html]);

  return (
    <iframe
      ref={ref}
      srcDoc={html}
      title={block.componentKey}
      sandbox="allow-same-origin"
      className="w-full block bg-transparent pointer-events-none"
      style={{ height, border: 0 }}
    />
  );
}

// Renders the site header or footer in a sandboxed iframe above / below the
// block stack so editors see the same chrome the published page shows.
function ChromeIframe({ kind, chrome, currentSlug, showFooterCta }: { kind: "header" | "footer"; chrome: NonNullable<SiteSettings["chrome"]>; currentSlug?: string; showFooterCta?: boolean }) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(kind === "header" ? 88 : 320);

  const html = useMemo(() => {
    const body = kind === "header" ? renderHeaderHtml(chrome, currentSlug) : renderFooterHtml(chrome, { showFooterCta });
    return `<!doctype html><html><head><meta charset="utf-8" /><meta name="viewport" content="width=device-width, initial-scale=1" /><link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Lora:ital,wght@0,400..700;1,400..700&family=Source+Sans+3:ital,wght@0,300..700;1,300..700&display=swap" /><link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.css" /><script src="https://cdn.jsdelivr.net/npm/@tailwindcss/browser@4"></script><style>${neueraCss as string}\n${gridShimCss as string}\nhtml,body{background:transparent !important;margin:0 !important;padding:0 !important;overflow-x:hidden}.nl-header{position:relative !important}.reveal,.reveal-scale,.reveal-on-scroll,.n-reveal,.n-reveal-scale,.n-reveal-left,.n-reveal-right,.n-reveal-blur{opacity:1 !important;transform:none !important;transition:none !important}</style></head><body>${body}</body></html>`;
  }, [kind, chrome, currentSlug, showFooterCta]);

  useLayoutEffect(() => {
    const iframe = ref.current;
    if (!iframe) return;
    let interval: ReturnType<typeof setInterval> | null = null;
    let stopTimer: ReturnType<typeof setTimeout> | null = null;
    let lastReported = 0;
    const measure = () => {
      const doc = iframe.contentDocument;
      if (!doc?.body) return;
      const h = Math.max(doc.documentElement.scrollHeight, doc.body.scrollHeight);
      if (h && h !== lastReported) { lastReported = h; setHeight(h); }
    };
    const start = () => {
      measure();
      if (interval) clearInterval(interval);
      interval = setInterval(measure, 200);
      if (stopTimer) clearTimeout(stopTimer);
      stopTimer = setTimeout(() => { if (interval) { clearInterval(interval); interval = null; } }, 4000);
    };
    iframe.addEventListener("load", start);
    if (iframe.contentDocument?.readyState === "complete") start();
    return () => { iframe.removeEventListener("load", start); if (interval) clearInterval(interval); if (stopTimer) clearTimeout(stopTimer); };
  }, [html]);

  return (
    <iframe
      ref={ref}
      srcDoc={html}
      title={kind}
      sandbox="allow-same-origin"
      className="w-full block bg-transparent pointer-events-none"
      style={{ height, border: 0 }}
    />
  );
}

function Renderer({ componentKey, props }: { componentKey: string; props: Record<string, unknown> }) {
  const text = (k: string) => String(props[k] ?? "");
  if (componentKey === "hero.centered") {
    return (
      <div className="py-16 px-8 text-center">
        <h2 className="display text-4xl md:text-5xl text-[var(--color-ink)] mb-3">{text("title")}</h2>
        <p className="text-[15px] text-[var(--color-muted)] max-w-md mx-auto">{text("subtitle")}</p>
      </div>
    );
  }
  if (componentKey === "hero.split") {
    return (
      <div className="grid grid-cols-2 gap-8 py-12 px-8 items-center">
        <div>
          <h2 className="display text-4xl text-[var(--color-ink)] mb-2">{text("title")}</h2>
          <p className="text-[14px] text-[var(--color-muted)] mb-4">{text("body")}</p>
          {props.ctaText && <span className="inline-flex items-center gap-1.5 h-9 px-4 rounded-[var(--radius-sm)] bg-[var(--color-ink)] text-[var(--color-paper)] text-[13px]">{text("ctaText")} →</span>}
        </div>
        <div className="aspect-video bg-[var(--color-paper-2)] rounded-[var(--radius-sm)] border border-[var(--color-rule)] bg-hatch" />
      </div>
    );
  }
  if (componentKey === "content.section") {
    return (
      <div className="py-10 px-8">
        <h3 className="display text-2xl text-[var(--color-ink)] mb-3">{text("heading")}</h3>
        {Array.isArray(props.paragraphs) && (props.paragraphs as string[]).map((p, i) => <p key={i} className="text-[14px] text-[var(--color-ink-2)] mb-2 max-w-prose">{p}</p>)}
      </div>
    );
  }
  if (componentKey === "content.richtext") {
    return <div className="py-8 px-8 prose prose-sm max-w-prose text-[var(--color-ink-2)]" dangerouslySetInnerHTML={{ __html: String(props.html ?? "") }} />;
  }
  if (componentKey === "content.feature_split") {
    const right = String(props.imagePosition ?? "right") === "right";
    return (
      <div className={cn("py-12 px-8 grid grid-cols-2 gap-8 items-center", !right && "[direction:rtl]")}>
        <div className="[direction:ltr]">
          <h3 className="display text-3xl text-[var(--color-ink)] mb-3">{text("heading")}</h3>
          <p className="text-[14px] text-[var(--color-ink-2)]">{text("description")}</p>
        </div>
        <div className="aspect-[4/3] bg-[var(--color-paper-2)] rounded-[var(--radius-sm)] border border-[var(--color-rule)] bg-hatch" />
      </div>
    );
  }
  if (componentKey === "content.image") {
    return (
      <figure className="py-8 px-8">
        <div className="aspect-video bg-[var(--color-paper-2)] rounded-[var(--radius-sm)] border border-[var(--color-rule)] bg-hatch flex items-center justify-center">
          <span className="font-mono text-[10.5px] text-[var(--color-muted)]">{text("assetId") || "no asset · pick one"}</span>
        </div>
        {props.caption && <figcaption className="text-[12px] text-[var(--color-muted)] mt-2 italic">{text("caption")}</figcaption>}
      </figure>
    );
  }
  if (componentKey === "cards.grid") {
    const cols = Number(props.columns ?? 3);
    const items = Array.isArray(props.items) ? (props.items as Array<{ heading?: string; body?: string }>) : [];
    return (
      <div className="py-10 px-8">
        <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
          {items.map((it, i) => (
            <div key={i} className="p-5 bg-[var(--color-paper-2)]/50 border border-[var(--color-rule)] rounded-[var(--radius-sm)]">
              <h4 className="font-medium text-[14px] text-[var(--color-ink)] mb-1">{it.heading}</h4>
              <p className="text-[12.5px] text-[var(--color-muted)]">{it.body}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (componentKey === "form.builder") {
    const fields = Array.isArray(props.fields) ? (props.fields as Array<{ name: string; label: string; kind: string }>) : [];
    return (
      <div className="py-10 px-8 max-w-md">
        {props.title && <h3 className="display text-2xl text-[var(--color-ink)] mb-4">{text("title")}</h3>}
        {fields.map((f) => (
          <label key={f.name} className="block mb-3">
            <span className="block text-[12px] text-[var(--color-ink-2)] mb-1">{f.label}</span>
            <input className="w-full h-9 px-3 bg-[var(--color-surface)] border border-[var(--color-rule)] rounded-[var(--radius-sm)] text-[13px]" disabled />
          </label>
        ))}
        <button className="inline-flex h-9 px-4 rounded-[var(--radius-sm)] bg-[var(--color-ink)] text-[var(--color-paper)] text-[13px] mt-2">{(props.submit as { label?: string } | undefined)?.label ?? "Send"}</button>
      </div>
    );
  }
  if (componentKey === "layout.spacer") {
    const h = String(props.height ?? "medium");
    const map = { small: 24, medium: 48, large: 80, xlarge: 120 } as Record<string, number>;
    return <div style={{ height: map[h] ?? 48 }} className={cn("mx-8", props.style === "divider" && "border-t border-[var(--color-rule)]", props.style === "dots" && "flex items-center justify-center text-[var(--color-mute-2)]")}>{props.style === "dots" ? "· · ·" : null}</div>;
  }
  return <div className="py-6 px-8 text-[11px] font-mono text-[var(--color-muted)]">Unknown block: {componentKey}</div>;
}

// ── Inspector ──────────────────────────────────────────────────────────────

function Inspector({ block, viewport, onChange, onDelete, onMoveUp, onMoveDown, onDuplicate }: {
  block: Block;
  viewport: "desktop" | "mobile";
  onChange: (b: Block) => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDuplicate: () => void;
}) {
  const { slug: siteSlug } = useParams();
  const [assetPicker, setAssetPicker] = useState(false);
  const sourceProps = viewport === "mobile" ? (block.mobile?.props ?? {}) : block.props;
  const setProp = (k: string, v: unknown) => {
    if (viewport === "mobile") {
      onChange({ ...block, mobile: { ...block.mobile, props: { ...(block.mobile?.props ?? {}), [k]: v } } });
    } else {
      onChange({ ...block, props: { ...block.props, [k]: v } });
    }
  };

  return (
    <div className="flex flex-col h-full">
      <header className="px-5 pt-4 pb-3 border-b border-[var(--color-rule)]">
        <div className="section-no mb-1">{viewport === "mobile" ? "Inspector · Mobile override" : "Inspector"}</div>
        <h3 className="display text-xl text-[var(--color-ink)]">{niceName(block.componentKey)}</h3>
        <Mono className="text-[10.5px] text-[var(--color-muted)]">{block.componentKey}</Mono>
      </header>

      <div className="flex-1 overflow-y-auto px-5 pt-4">
        {block.componentKey === "content.image" && (
          <>
            <Label>Asset</Label>
            <div className="flex gap-2 mb-4">
              <input value={String(sourceProps.assetId ?? block.props.assetId ?? "")} onChange={(e) => setProp("assetId", e.target.value)} placeholder="asset id" className="flex-1 h-9 px-3 text-[13px] border border-[var(--color-rule)] rounded-[var(--radius-sm)] bg-[var(--color-surface)] focus:outline-none focus:border-[var(--color-ink)]" />
              <Button type="button" variant="ghost" size="sm" onClick={() => setAssetPicker(true)}>Pick…</Button>
            </div>
            <AssetPicker siteSlug={siteSlug!} open={assetPicker} onClose={() => setAssetPicker(false)} onPick={(a) => { setProp("assetId", a.id); if (a.alt && !block.props.alt) setProp("alt", a.alt); }} />
          </>
        )}

        {Object.entries(defaultPropsFor(block.componentKey)).map(([k]) => {
          if (k === "assetId" && block.componentKey === "content.image") return null;
          const desktopVal = (block.props as Record<string, unknown>)[k];
          const v = (sourceProps as Record<string, unknown>)[k] ?? (viewport === "mobile" ? desktopVal : "");
          if (typeof v === "boolean" || typeof desktopVal === "boolean") {
            return (
              <label key={k} className="flex items-center gap-2 mb-3">
                <input type="checkbox" checked={!!v} onChange={(e) => setProp(k, e.target.checked)} />
                <span className="text-[13px] text-[var(--color-ink-2)]">{k}</span>
              </label>
            );
          }
          if (typeof v === "number" || typeof desktopVal === "number") {
            return <Field key={k} label={k} type="number" value={String(v ?? "")} onChange={(e) => setProp(k, e.target.value === "" ? null : Number(e.target.value))} />;
          }
          if (Array.isArray(v) || (typeof v === "object" && v !== null) || Array.isArray(desktopVal) || (typeof desktopVal === "object" && desktopVal !== null)) {
            return (
              <Textarea
                key={k}
                label={`${k} · JSON`}
                rows={5}
                defaultValue={JSON.stringify(v ?? desktopVal, null, 2)}
                onBlur={(e) => { try { setProp(k, JSON.parse(e.target.value)); } catch { /* keep */ } }}
              />
            );
          }
          return <Field key={k} label={k} value={String(v ?? "")} onChange={(e) => setProp(k, e.target.value)} />;
        })}

        <hr className="rule my-5" />

        <div className="label-tracked mb-2">Visibility</div>
        <label className="flex items-center gap-2 mb-1.5">
          <input type="checkbox" checked={block.visibility?.desktop !== false} onChange={(e) => onChange({ ...block, visibility: { ...block.visibility, desktop: e.target.checked } })} />
          <span className="text-[13px]">Show on desktop</span>
        </label>
        <label className="flex items-center gap-2 mb-1.5">
          <input type="checkbox" checked={block.visibility?.mobile !== false} onChange={(e) => onChange({ ...block, visibility: { ...block.visibility, mobile: e.target.checked } })} />
          <span className="text-[13px]">Show on mobile</span>
        </label>
      </div>

      <footer className="border-t border-[var(--color-rule)] px-5 py-3 flex items-center justify-between gap-2 bg-[var(--color-paper-2)]/60">
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={onMoveUp}>↑</Button>
          <Button variant="ghost" size="sm" onClick={onMoveDown}>↓</Button>
          <Button variant="ghost" size="sm" onClick={onDuplicate}>Duplicate</Button>
        </div>
        <Button variant="danger" size="sm" onClick={onDelete}>Delete</Button>
      </footer>
    </div>
  );
}

function PageSettings({ metadata, onChange }: { metadata: PageMetadata; onChange: (m: Partial<PageMetadata>) => void }) {
  return (
    <div className="px-5 pt-5 pb-8">
      <div className="section-no mb-1">Page · Metadata</div>
      <h3 className="display text-xl text-[var(--color-ink)] mb-1">Page settings</h3>
      <p className="text-[12px] text-[var(--color-muted)] mb-5">Select a block above to edit its props, or use this panel for the page itself.</p>

      <Field label="Title" value={metadata.title} onChange={(e) => onChange({ title: e.target.value })} />
      <Field label="Slug" value={metadata.slug} onChange={(e) => onChange({ slug: e.target.value })} />
      <Field label="Meta description" value={metadata.description ?? ""} onChange={(e) => onChange({ description: e.target.value })} help="Falls back to the site default if empty." />
      <Field label="Canonical URL" value={metadata.canonical ?? ""} onChange={(e) => onChange({ canonical: e.target.value || null })} />
      <label className="flex items-center gap-2 mb-3">
        <input type="checkbox" checked={!!metadata.noindex} onChange={(e) => onChange({ noindex: e.target.checked })} />
        <span className="text-[13px]">noindex (block search engines)</span>
      </label>
    </div>
  );
}
